import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, AlertCircle, RefreshCw, Loader2, Clock, Maximize2, Minimize2, FileDown } from 'lucide-react';
import { useTimesheetOverview } from '../../hooks/useTimesheetOverview';
import {
  getDatePresetRange,
  formatHours,
  formatVariance,
  statusToBadgeVariant,
} from '../../utils/timesheetHelpers';
import {
  BrioPageHeader,
  BrioFrame,
  BrioAnalyticsSurface,
  BrioTable,
  BrioTableWrapper,
  BrioKpiBlock,
  BrioBadge,
  BrioColorIndicator,
  BrioGrid,
  BrioDateRangePicker,
  BrioEmptyState,
  getTablePropsFromCode,
} from '../../components/BrioComponents';

const PRESENTATION_CODE = '1b'; // compact table

const DATE_PRESETS = [
  { value: 'this-week', label: 'This week' },
  { value: 'last-week', label: 'Last week' },
  { value: 'last-2-weeks', label: 'Last 2 weeks' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'last-3-months', label: 'Last 3 months' },
  { value: 'last-year', label: 'Last year' },
  { value: 'custom', label: 'Custom range' },
];

const MONTHLY_PRESETS = new Set(['this-month', 'last-month', 'last-3-months', 'last-year']);

// ═══════════════════════════════════════════════════════════
// PDF EXPORT — builds a print-ready HTML document of the current
// view and opens it in a new window with the print dialog.
// Mirrors the codebase pattern (open window → write HTML → print).
// ═══════════════════════════════════════════════════════════

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Groups consecutive date columns by month/year so a spanning header row can
// be rendered above the day/week columns. Accepts columns keyed by `date`
// (daily view) or `weekStart` (monthly view).
function buildMonthGroups(cols) {
  const groups = [];
  for (const c of (cols || [])) {
    const dateStr = c.date || c.weekStart;
    if (!dateStr) continue;
    const d = new Date(dateStr + 'T00:00:00');
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.span += 1;
    else groups.push({ key, label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`, span: 1 });
  }
  return groups;
}

function exportStatusColor(status) {
  if (status === 'green') return '#2e7d32';
  if (status === 'amber') return '#b8860b';
  return '#c62828';
}

function exportVarianceStyle(value) {
  if (Math.abs(value) < 0.05) return '';
  return value > 0 ? 'color:#2e7d32;font-weight:600;' : 'color:#c62828;font-weight:600;';
}

function buildTimesheetExportHtml({ rows, dayColumns, weekColumns, kpis, period, groupBy, expandedRows, dateRangeLabel, filters, generatedAt }) {
  const isWeekly = period === 'weekly';
  const isTeam = groupBy === 'team';
  const cols = (isWeekly ? dayColumns : weekColumns) || [];
  const periodLabel = isWeekly ? 'Weekly view (daily breakdown)' : 'Monthly view (weekly breakdown)';
  const groupLabel = isTeam ? 'By team member' : 'By project';

  // Scale font/padding down as the column count grows so wide selections
  // (e.g. monthly over many weeks) still fit the A4 landscape page width.
  const fixedCols = isTeam ? 7 : 3; // label + summary columns
  const totalCols = fixedCols + cols.length;
  let fs, headFs, padV, padH, firstColW;
  if (totalCols <= 16) { fs = 9.5; headFs = 9; padV = 4; padH = 5; firstColW = '16%'; }
  else if (totalCols <= 24) { fs = 8; headFs = 7.5; padV = 3; padH = 4; firstColW = '14%'; }
  else if (totalCols <= 34) { fs = 6.5; headFs = 6; padV = 2; padH = 3; firstColW = '12%'; }
  else { fs = 5.5; headFs = 5; padV = 1; padH = 2; firstColW = '10%'; }

  const dim = 'color:#bbb;';
  const numTd = 'text-align:right;';
  const cell = (h) => `<td style="${numTd}${h === 0 ? dim : ''}">${h === 0 ? '–' : formatHours(h)}</td>`;

  const colHeads = cols
    .map(c => `<th style="text-align:right;${c.isWeekend ? 'color:#999;' : ''}">${escapeHtml(c.label)}</th>`)
    .join('');

  // Month/year grouping row that spans the date columns.
  const summaryCount = isTeam ? 6 : 2;
  const monthGroups = buildMonthGroups(cols);
  const monthHeadRow = `<th></th>${monthGroups
    .map(g => `<th colspan="${g.span}" style="text-align:center;color:#9F902A;font-weight:700;border-left:1px solid #e5e5e5;">${escapeHtml(g.label)}</th>`)
    .join('')}<th colspan="${summaryCount}"></th>`;

  let headRow, bodyRows;
  if (isTeam) {
    headRow = `<th style="text-align:left;">Name</th>${colHeads}<th style="${numTd}">Total</th><th style="${numTd}">Alloc</th><th style="${numTd}">Target</th><th style="${numTd}">vs Alloc</th><th style="${numTd}">vs Trgt</th><th style="text-align:center;">Status</th>`;
    bodyRows = rows.map(row => {
      const cells = (isWeekly ? row.dailyHours : row.weekTotals) || [];
      const total = isWeekly ? row.weekLogged : row.monthLogged;
      const alloc = isWeekly ? row.weekAllocated : row.monthAllocated;
      const target = isWeekly ? row.weekContracted : row.monthContracted;
      const cellHtml = cells.map(cell).join('');
      const nameHtml = `${escapeHtml(row.userName)}${row.isInactive ? ' <span style="font-size:0.8em;color:#999;font-weight:400;">(inactive)</span>' : ''}`;
      let tr = `<tr><td style="text-align:left;font-weight:600;">${nameHtml}</td>${cellHtml}<td style="${numTd}font-weight:700;">${formatHours(total)}</td><td style="${numTd}">${formatHours(alloc)}</td><td style="${numTd}">${formatHours(target)}</td><td style="${numTd}${exportVarianceStyle(row.vsAllocated)}">${formatVariance(row.vsAllocated)}</td><td style="${numTd}${exportVarianceStyle(row.vsContracted)}">${formatVariance(row.vsContracted)}</td><td style="text-align:center;color:${exportStatusColor(row.status)};font-weight:600;">${Math.round(row.completionPct)}%</td></tr>`;
      if (isWeekly && expandedRows?.has(row.userId) && row.projectRows?.length) {
        tr += row.projectRows.map(pr => {
          const pCells = (pr.dailyHours || []).map(cell).join('');
          return `<tr style="background:#fafafa;font-size:0.9em;"><td style="text-align:left;padding-left:18px;">${escapeHtml(pr.projectName)}</td>${pCells}<td style="${numTd}font-weight:600;">${formatHours(pr.total)}</td><td style="${numTd}">${formatHours(pr.allocated)}</td><td colspan="4"></td></tr>`;
        }).join('');
      }
      return tr;
    }).join('');
  } else {
    headRow = `<th style="text-align:left;">Project</th>${colHeads}<th style="${numTd}">Total</th><th style="${numTd}">Alloc</th>`;
    bodyRows = rows.map(row => {
      const cells = (isWeekly ? row.dailyHours : row.weekTotals) || [];
      const cellHtml = cells.map(cell).join('');
      let tr = `<tr><td style="text-align:left;font-weight:600;">${escapeHtml(row.projectName)}</td>${cellHtml}<td style="${numTd}font-weight:700;">${formatHours(row.total)}</td><td style="${numTd}">${formatHours(row.allocated)}</td></tr>`;
      if (isWeekly && expandedRows?.has(row.projectId) && row.userRows?.length) {
        tr += row.userRows.map(ur => {
          const uCells = (ur.dailyHours || []).map(cell).join('');
          return `<tr style="background:#fafafa;font-size:0.9em;"><td style="text-align:left;padding-left:18px;">${escapeHtml(ur.userName)}</td>${uCells}<td style="${numTd}font-weight:600;">${formatHours(ur.total)}</td><td style="${numTd}">${formatHours(ur.allocated)}</td></tr>`;
        }).join('');
      }
      return tr;
    }).join('');
  }

  const kpiHtml = (isTeam && kpis) ? `
    <div class="kpis">
      <div class="kpi"><div class="kpi-label">Team Logged</div><div class="kpi-value">${formatHours(kpis.totalLogged)}h</div></div>
      <div class="kpi"><div class="kpi-label">Team Allocated</div><div class="kpi-value">${formatHours(kpis.totalAllocated)}h</div></div>
      <div class="kpi"><div class="kpi-label">Completion</div><div class="kpi-value">${Math.round(kpis.completionPct)}%</div></div>
      <div class="kpi"><div class="kpi-label">Incomplete</div><div class="kpi-value">${kpis.incompleteCount}</div></div>
    </div>` : '';

  const genLabel = generatedAt.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const filterLine = (filters && filters.length)
    ? `<div class="meta-filters">${filters.map(f => `<span>${escapeHtml(f)}</span>`).join('')}</div>`
    : '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Timesheet Overview</title>
<link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: A4 landscape; margin: 10mm; }
  * { box-sizing: border-box; }
  body { margin:0; background:#fff; font-family:'Raleway',sans-serif; color:#1a1a1a; font-size:${fs}px; }
  .doc { padding: 8px 0; }
  .head { border-bottom:2px solid #9F902A; padding-bottom:8px; margin-bottom:12px; }
  .head h1 { font-size:16px; font-weight:700; margin:0 0 2px; letter-spacing:0.04em; }
  .head .sub { font-size:11px; color:#555; }
  .head .meta { font-size:10px; color:#777; margin-top:4px; }
  .meta-filters { margin-top:6px; display:flex; flex-wrap:wrap; gap:6px; }
  .meta-filters span { background:#f3f1e6; border:1px solid #e3dfc8; padding:1px 7px; font-size:9px; color:#5a531c; }
  .kpis { display:flex; gap:10px; margin-bottom:12px; }
  .kpi { flex:1; border:1px solid #e5e5e5; padding:8px 10px; }
  .kpi-label { font-size:9px; text-transform:uppercase; letter-spacing:0.06em; color:#888; }
  .kpi-value { font-size:15px; font-weight:700; margin-top:2px; }
  table { width:100%; border-collapse:collapse; table-layout:fixed; }
  thead { display:table-header-group; }
  thead th { font-size:${headFs}px; text-transform:uppercase; letter-spacing:0.02em; color:#666; border-bottom:1.5px solid #333; padding:${padV}px ${padH}px; word-break:break-word; overflow-wrap:anywhere; vertical-align:bottom; }
  tbody td { padding:${padV}px ${padH}px; border-bottom:1px solid #ededed; word-break:break-word; overflow-wrap:anywhere; }
  tbody tr { page-break-inside:avoid; }
  .gen { margin-top:14px; font-size:8px; color:#aaa; text-align:right; }
</style></head>
<body>
  <div class="doc">
    <div class="head">
      <h1>Timesheet Overview</h1>
      <div class="sub">${escapeHtml(dateRangeLabel || '')}</div>
      <div class="meta">${periodLabel} · ${groupLabel}</div>
      ${filterLine}
    </div>
    ${kpiHtml}
    <table>
      <colgroup><col style="width:${firstColW}" /></colgroup>
      <thead><tr>${monthHeadRow}</tr><tr>${headRow}</tr></thead>
      <tbody>${bodyRows}</tbody>
    </table>
    <div class="gen">Generated ${escapeHtml(genLabel)} · TimeTracker Pro</div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
</body></html>`;
}

function TimesheetOverviewPage({
  currentUser,
  activeUsers,
  activeProjects,
  activeTimeEntries,
  useSupabaseDB,
  activeTeams,
  activeHolidays,
  activeTimeOff,
  getProjectIdsForTeam,
  dataLoading = false,
}) {
  // Filter state
  const [period, setPeriod] = useState('weekly');
  const [groupBy, setGroupBy] = useState('team');
  const [datePreset, setDatePreset] = useState('this-week');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [filterTeamId, setFilterTeamId] = useState(null);
  const [filterUserId, setFilterUserId] = useState(null);
  const [filterProjectId, setFilterProjectId] = useState(null);
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const [varianceOnly, setVarianceOnly] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  // Fullscreen mode — mirrors InquiriesPage pattern
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Lock body scroll while fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = orig; };
  }, [isFullscreen]);

  // Escape key exits fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e) => { if (e.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);

  // Compute date range
  const { start: rangeStart, end: rangeEnd } = useMemo(() => {
    if (datePreset === 'custom' && customStart && customEnd) {
      return { start: customStart, end: customEnd };
    }
    return getDatePresetRange(datePreset);
  }, [datePreset, customStart, customEnd]);

  // Handle date preset change with auto-switching
  const handlePresetChange = useCallback((value) => {
    setDatePreset(value);
    setExpandedRows(new Set());
    if (MONTHLY_PRESETS.has(value)) {
      setPeriod('monthly');
    } else if (value === 'this-week' || value === 'last-week') {
      setPeriod('weekly');
    }
  }, []);

  // Hook
  const { rows, kpis, dayColumns, weekColumns, isLoading: innerLoading, refresh, userEffectiveTeam } = useTimesheetOverview({
    users: activeUsers || [],
    timeEntries: activeTimeEntries || [],
    useSupabaseDB: !!useSupabaseDB,
    teams: activeTeams || [],
    holidays: activeHolidays || [],
    timeOff: activeTimeOff || [],
    projects: activeProjects || [],
    currentUser,
    startDate: rangeStart,
    endDate: rangeEnd,
    period,
    groupBy,
    filterTeamId,
    filterUserId,
    filterProjectId,
    incompleteOnly,
    varianceOnly,
    getProjectIdsForTeam,
  });

  // Combined loading + last-synced tracking
  const combinedLoading = dataLoading || innerLoading;
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !combinedLoading) {
      setLastSyncedAt(new Date());
    }
    prevLoadingRef.current = combinedLoading;
  }, [combinedLoading]);
  const syncedLabel = lastSyncedAt
    ? lastSyncedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : null;

  // Sort
  const sortedRows = useMemo(() => {
    if (!sortField) return rows;
    return [...rows].sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'name': va = a.userName || a.projectName || ''; vb = b.userName || b.projectName || ''; break;
        case 'total': va = a.weekLogged ?? a.monthLogged ?? a.total ?? 0; vb = b.weekLogged ?? b.monthLogged ?? b.total ?? 0; break;
        case 'vsAlloc': va = a.vsAllocated ?? 0; vb = b.vsAllocated ?? 0; break;
        case 'vsCntrt': va = a.vsContracted ?? 0; vb = b.vsContracted ?? 0; break;
        case 'status': va = a.completionPct ?? 0; vb = b.completionPct ?? 0; break;
        default: return 0;
      }
      if (typeof va === 'string') {
        const cmp = va.localeCompare(vb);
        return sortDir === 'asc' ? cmp : -cmp;
      }
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }, [rows, sortField, sortDir]);

  const toggleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }, [sortField]);

  const toggleExpand = useCallback((id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Teams list for filter dropdown
  const teamOptions = useMemo(() => {
    if (!activeTeams) return [];
    return activeTeams.filter(t => t.isActive !== false).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [activeTeams]);

  // User IDs that logged time within the selected range — used to keep
  // members who are inactive *now* selectable when they were active during
  // the selected period (mirrors the same rule in useTimesheetOverview).
  const userIdsWithEntriesInRange = useMemo(() => {
    const set = new Set();
    if (!rangeStart || !rangeEnd) return set;
    for (const e of (activeTimeEntries || [])) {
      if (!e.date || e.date < rangeStart || e.date > rangeEnd) continue;
      set.add(Number(e.userId));
    }
    return set;
  }, [activeTimeEntries, rangeStart, rangeEnd]);

  // Users list for filter dropdown
  const userOptions = useMemo(() => {
    if (!activeUsers) return [];
    let filtered = activeUsers.filter(u => u.isActive !== false || userIdsWithEntriesInRange.has(Number(u.id)));
    if (filterTeamId) {
      const ftid = Number(filterTeamId);
      filtered = filtered.filter(u => {
        const effectiveTeam = userEffectiveTeam?.get(Number(u.id));
        return effectiveTeam === ftid;
      });
    }
    return filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [activeUsers, filterTeamId, userEffectiveTeam, userIdsWithEntriesInRange]);

  // Projects list for filter dropdown
  const projectOptions = useMemo(() => {
    if (!activeProjects) return [];
    return activeProjects.filter(p => p.isActive !== false).sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || ''));
  }, [activeProjects]);

  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);

  // Sticky first-column styles for horizontal scroll
  const stickyThStyle = { position: 'sticky', left: 0, zIndex: 2, background: 'var(--brio-grey-light, #f5f5f5)', textAlign: 'left' };
  const stickyTdStyle = { position: 'sticky', left: 0, zIndex: 1, background: 'inherit', textAlign: 'left', whiteSpace: 'nowrap' };

  // Subtitle
  const subtitle = useMemo(() => {
    if (!rangeStart || !rangeEnd) return '';
    const fmt = (s) => {
      const d = new Date(s + 'T00:00:00');
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    return `${fmt(rangeStart)} – ${fmt(rangeEnd)}`;
  }, [rangeStart, rangeEnd]);

  // Export current view to PDF (opens print dialog in a new window)
  const handleExportPDF = useCallback(() => {
    if (!sortedRows.length) return;
    const win = window.open('', '_blank');
    if (!win) { alert('Allow popups to export the timesheet to PDF.'); return; }

    const filters = [];
    if (filterTeamId) {
      const t = (activeTeams || []).find(x => Number(x.id) === Number(filterTeamId));
      if (t) filters.push(`Team: ${t.name}`);
    }
    if (filterUserId) {
      const u = (activeUsers || []).find(x => Number(x.id) === Number(filterUserId));
      if (u) filters.push(`Member: ${u.name || u.email}`);
    }
    if (filterProjectId) {
      const p = (activeProjects || []).find(x => Number(x.id) === Number(filterProjectId));
      if (p) filters.push(`Project: ${p.projectCode ? `${p.projectCode} — ${p.name}` : p.name}`);
    }
    if (incompleteOnly) filters.push('Incomplete only');
    if (varianceOnly) filters.push('Variance only');

    const html = buildTimesheetExportHtml({
      rows: sortedRows,
      dayColumns,
      weekColumns,
      kpis,
      period,
      groupBy,
      expandedRows,
      dateRangeLabel: subtitle,
      filters,
      generatedAt: new Date(),
    });
    win.document.write(html);
    win.document.close();
  }, [sortedRows, dayColumns, weekColumns, kpis, period, groupBy, expandedRows, subtitle, filterTeamId, filterUserId, filterProjectId, incompleteOnly, varianceOnly, activeTeams, activeUsers, activeProjects]);

  // Variance color style
  const varianceStyle = (value) => {
    if (Math.abs(value) < 0.05) return {};
    return { color: value > 0 ? 'var(--brio-green)' : 'var(--brio-red)', fontWeight: 600 };
  };

  const dimStyle = { color: 'var(--brio-grey)', opacity: 0.5 };

  // Sortable header
  const SortHeader = ({ field, children, align, style: extraStyle }) => (
    <th
      style={{ cursor: 'pointer', textAlign: align || 'right', userSelect: 'none', whiteSpace: 'nowrap', ...extraStyle }}
      onClick={() => toggleSort(field)}
    >
      {children}
      {sortField === field && (sortDir === 'asc' ? ' ↑' : ' ↓')}
    </th>
  );

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════

  const headerActions = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <button
        onClick={handleExportPDF}
        disabled={sortedRows.length === 0}
        className="brio-btn brio-btn-secondary"
        title="Export the current view to PDF"
        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', opacity: sortedRows.length === 0 ? 0.5 : 1, cursor: sortedRows.length === 0 ? 'not-allowed' : 'pointer' }}
      >
        <FileDown className="w-4 h-4" /> Export PDF
      </button>
      <button
        onClick={() => setIsFullscreen(f => !f)}
        className="brio-btn brio-btn-secondary"
        title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
        style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px' }}
      >
        {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <div
      className={isFullscreen ? 'timesheet-fullscreen' : ''}
      style={isFullscreen
        ? { position: 'fixed', inset: 0, zIndex: 10000, background: 'var(--brio-bg, #ffffff)', display: 'flex', flexDirection: 'column', overflow: 'hidden', width: '100vw', height: '100vh' }
        : undefined
      }
    >
      {/* Fullscreen: override ALL max-width so content + tables go edge-to-edge */}
      {isFullscreen && (
        <style>{`
          .timesheet-fullscreen .brio-frame,
          .timesheet-fullscreen [class*="brio-frame"] {
            max-width: 100% !important;
            width: 100% !important;
            padding-left: 24px !important;
            padding-right: 24px !important;
          }
          .timesheet-fullscreen .brio-table-wrap,
          .timesheet-fullscreen [class*="brio-table-wrap"] {
            max-width: 100% !important;
          }
          .timesheet-fullscreen .brio-table {
            min-width: 0 !important;
            width: 100% !important;
          }
          .timesheet-fullscreen .brio-analytics-surface {
            max-width: 100% !important;
          }
        `}</style>
      )}

      <BrioPageHeader title={subtitle} right={headerActions} />

      {/* Scrollable body wrapper for fullscreen mode */}
      <div style={isFullscreen ? { flex: 1, overflowY: 'auto', overflowX: 'hidden', width: '100%' } : {}}>

      {/* ── Filter Bar ── */}
      <div className="brio-pageband brio-pageband--filter" style={{ background: 'var(--brio-grey-light, #f5f5f5)' }}>
        <BrioFrame>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', padding: '10px 0' }}>
            {/* Period toggle */}
            <div style={{ display: 'flex', border: '1px solid var(--brio-border)', borderRadius: 0 }}>
              <button
                onClick={() => { setPeriod('weekly'); setExpandedRows(new Set()); }}
                style={{
                  padding: '4px 12px', fontSize: '11px', fontFamily: 'Raleway, sans-serif',
                  background: period === 'weekly' ? 'var(--brio-gold)' : 'white',
                  color: period === 'weekly' ? 'white' : 'var(--brio-black)',
                  border: 'none', cursor: 'pointer',
                }}
              >Daily</button>
              <button
                onClick={() => { setPeriod('monthly'); setExpandedRows(new Set()); }}
                style={{
                  padding: '4px 12px', fontSize: '11px', fontFamily: 'Raleway, sans-serif',
                  background: period === 'monthly' ? 'var(--brio-gold)' : 'white',
                  color: period === 'monthly' ? 'white' : 'var(--brio-black)',
                  border: 'none', borderLeft: '1px solid var(--brio-border)', cursor: 'pointer',
                }}
              >Weekly</button>
            </div>

            {/* Group by toggle */}
            <div style={{ display: 'flex', border: '1px solid var(--brio-border)', borderRadius: 0 }}>
              <button
                onClick={() => { setGroupBy('team'); setExpandedRows(new Set()); }}
                style={{
                  padding: '4px 12px', fontSize: '11px', fontFamily: 'Raleway, sans-serif',
                  background: groupBy === 'team' ? 'var(--brio-gold)' : 'white',
                  color: groupBy === 'team' ? 'white' : 'var(--brio-black)',
                  border: 'none', cursor: 'pointer',
                }}
              >Team Member</button>
              <button
                onClick={() => { setGroupBy('project'); setExpandedRows(new Set()); }}
                style={{
                  padding: '4px 12px', fontSize: '11px', fontFamily: 'Raleway, sans-serif',
                  background: groupBy === 'project' ? 'var(--brio-gold)' : 'white',
                  color: groupBy === 'project' ? 'white' : 'var(--brio-black)',
                  border: 'none', borderLeft: '1px solid var(--brio-border)', cursor: 'pointer',
                }}
              >Project</button>
            </div>

            {/* Date preset */}
            <select
              value={datePreset}
              onChange={(e) => handlePresetChange(e.target.value)}
              className="brio-select"
              style={{ fontSize: '11px', padding: '4px 8px', minWidth: '120px' }}
            >
              {DATE_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>

            {/* Custom date range */}
            {datePreset === 'custom' && (
              <BrioDateRangePicker
                startDate={customStart}
                endDate={customEnd}
                onChangeStart={setCustomStart}
                onChangeEnd={setCustomEnd}
                placeholder="Select range"
              />
            )}

            {/* Team filter */}
            <select
              value={filterTeamId || ''}
              onChange={(e) => { setFilterTeamId(e.target.value ? Number(e.target.value) : null); setFilterUserId(null); }}
              className="brio-select"
              style={{ fontSize: '11px', padding: '4px 8px', minWidth: '100px' }}
            >
              <option value="">All teams</option>
              {teamOptions.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>

            {/* Member filter */}
            <select
              value={filterUserId || ''}
              onChange={(e) => setFilterUserId(e.target.value ? Number(e.target.value) : null)}
              className="brio-select"
              style={{ fontSize: '11px', padding: '4px 8px', minWidth: '120px' }}
            >
              <option value="">All members</option>
              {userOptions.map(u => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>

            {/* Project filter */}
            <select
              value={filterProjectId || ''}
              onChange={(e) => setFilterProjectId(e.target.value ? Number(e.target.value) : null)}
              className="brio-select"
              style={{ fontSize: '11px', padding: '4px 8px', minWidth: '120px' }}
            >
              <option value="">All projects</option>
              {projectOptions.map(p => (
                <option key={p.id} value={p.id}>{p.projectCode ? `${p.projectCode} \u2014 ${p.name}` : p.name}</option>
              ))}
            </select>

            {/* Toggle filters */}
            <label
              title="Show only team members who have not yet reached 95% of their target hours"
              style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', cursor: 'pointer' }}
            >
              <input type="checkbox" checked={incompleteOnly} onChange={() => setIncompleteOnly(!incompleteOnly)} />
              Incomplete only
            </label>

            <label
              title="Show only team members whose logged hours differ from their allocated or target hours"
              style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontFamily: 'Raleway, sans-serif', cursor: 'pointer' }}
            >
              <input type="checkbox" checked={varianceOnly} onChange={() => setVarianceOnly(!varianceOnly)} />
              Variance only
            </label>
          </div>

          {/* ── Sync status ── */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            paddingBottom: '8px', fontSize: '11px',
            fontFamily: 'Raleway, sans-serif', color: 'var(--brio-grey)',
          }}>
            {combinedLoading ? (
              <>
                <Loader2 size={12} className="animate-spin" style={{ color: 'var(--brio-gold)' }} />
                <span>Syncing…</span>
              </>
            ) : (
              <>
                <Clock size={12} />
                <span>{syncedLabel ? `Synced at ${syncedLabel}` : 'Ready'}</span>
              </>
            )}
            <button
              onClick={refresh}
              disabled={combinedLoading}
              title="Refresh data"
              style={{
                marginLeft: '4px', display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '2px 8px', fontSize: '11px', fontFamily: 'Raleway, sans-serif',
                background: 'none', border: '1px solid var(--brio-border)', borderRadius: 0,
                cursor: combinedLoading ? 'not-allowed' : 'pointer',
                opacity: combinedLoading ? 0.5 : 1, color: 'var(--brio-black)',
              }}
            >
              <RefreshCw size={11} style={combinedLoading ? { animation: 'spin 1s linear infinite' } : {}} />
              Refresh
            </button>
          </div>
        </BrioFrame>
      </div>

      {/* ── KPI Row ── */}
      {groupBy === 'team' && (
        <BrioFrame>
          <div style={{ padding: '16px 0 8px' }}>
            <BrioGrid cols={4} gap="s">
              <BrioKpiBlock label="Team Logged" value={`${formatHours(kpis.totalLogged)}h`} />
              <BrioKpiBlock label="Team Allocated" value={`${formatHours(kpis.totalAllocated)}h`} />
              <BrioKpiBlock label="Completion" value={`${Math.round(kpis.completionPct)}%`} />
              <BrioKpiBlock
                label="Incomplete"
                value={
                  <span style={{ color: kpis.incompleteCount > 0 ? 'var(--brio-red)' : 'var(--brio-green)' }}>
                    {kpis.incompleteCount}
                  </span>
                }
              />
            </BrioGrid>
          </div>
        </BrioFrame>
      )}

      {/* ── Table ── */}
      <BrioAnalyticsSurface padding="m">
        <div style={isFullscreen
          ? { maxWidth: '100%', width: '100%', margin: 0 }
          : { maxWidth: period === 'weekly' ? '1200px' : 'var(--brio-maxw-table)', margin: '0 auto' }
        }>
          <BrioTableWrapper style={{ overflowX: 'auto' }}>
            {sortedRows.length === 0 ? (
              <BrioEmptyState>No timesheet data found for the selected filters.</BrioEmptyState>
            ) : period === 'weekly' && groupBy === 'team' ? (
              <WeeklyTeamTable
                rows={sortedRows}
                dayColumns={dayColumns}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                tableProps={tableProps}
                varianceStyle={varianceStyle}
                dimStyle={dimStyle}
                SortHeader={SortHeader}
                stickyThStyle={stickyThStyle}
                stickyTdStyle={stickyTdStyle}
              />
            ) : period === 'weekly' && groupBy === 'project' ? (
              <WeeklyProjectTable
                rows={sortedRows}
                dayColumns={dayColumns}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                tableProps={tableProps}
                varianceStyle={varianceStyle}
                dimStyle={dimStyle}
                stickyThStyle={stickyThStyle}
                stickyTdStyle={stickyTdStyle}
              />
            ) : period === 'monthly' && groupBy === 'team' ? (
              <MonthlyTeamTable
                rows={sortedRows}
                weekColumns={weekColumns}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                tableProps={tableProps}
                varianceStyle={varianceStyle}
                SortHeader={SortHeader}
                stickyThStyle={stickyThStyle}
                stickyTdStyle={stickyTdStyle}
              />
            ) : (
              <MonthlyProjectTable
                rows={sortedRows}
                weekColumns={weekColumns}
                expandedRows={expandedRows}
                toggleExpand={toggleExpand}
                tableProps={tableProps}
                stickyThStyle={stickyThStyle}
                stickyTdStyle={stickyTdStyle}
              />
            )}
          </BrioTableWrapper>
        </div>
      </BrioAnalyticsSurface>

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MONTH / YEAR GROUPING HEADER ROW
// Spans the date columns so each month/year is labelled above the days.
// ═══════════════════════════════════════════════════════════

function MonthGroupHeaderRow({ cols, summaryCount, stickyThStyle }) {
  const groups = buildMonthGroups(cols);
  if (groups.length === 0) return null;
  return (
    <tr>
      <th style={stickyThStyle}></th>
      {groups.map(g => (
        <th
          key={g.key}
          colSpan={g.span}
          style={{
            textAlign: 'center', fontWeight: 700, color: 'var(--brio-gold)',
            whiteSpace: 'nowrap', fontSize: '10px',
            borderLeft: '1px solid var(--brio-border)',
            borderBottom: '1px solid var(--brio-border)',
          }}
        >
          {g.label}
        </th>
      ))}
      <th colSpan={summaryCount}></th>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════
// WEEKLY TEAM TABLE
// ═══════════════════════════════════════════════════════════

function WeeklyTeamTable({ rows, dayColumns, expandedRows, toggleExpand, tableProps, varianceStyle, dimStyle, SortHeader, stickyThStyle, stickyTdStyle }) {
  return (
    <BrioTable {...tableProps}>
      <thead>
        <MonthGroupHeaderRow cols={dayColumns} summaryCount={6} stickyThStyle={stickyThStyle} />
        <tr>
          <SortHeader field="name" align="left" style={stickyThStyle}>Name</SortHeader>
          {dayColumns.map(col => (
            <th key={col.date} style={{
              textAlign: 'right', whiteSpace: 'nowrap',
              ...(col.isWeekend ? { color: 'var(--brio-grey)', opacity: 0.7 } : {}),
              ...(col.isHoliday ? { background: 'var(--brio-gold-light)' } : {}),
            }}>
              {col.label}
            </th>
          ))}
          <SortHeader field="total">Total</SortHeader>
          <th style={{ textAlign: 'right' }}>Alloc</th>
          <th style={{ textAlign: 'right' }} title="Contracted hours adjusted for approved leave and join/end dates">Target</th>
          <SortHeader field="vsAlloc">vs Alloc</SortHeader>
          <SortHeader field="vsCntrt">vs Trgt</SortHeader>
          <SortHeader field="status">Status</SortHeader>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const isExpanded = expandedRows.has(row.userId);
          return (
            <React.Fragment key={row.userId}>
              <tr
                onClick={() => toggleExpand(row.userId)}
                style={{ cursor: 'pointer', background: '#fff' }}
              >
                <td style={stickyTdStyle}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <BrioColorIndicator color={row.teamColor} size="compact" />
                    {row.userName}
                    {row.isInactive && (
                      <span
                        style={{ fontSize: '10px', color: 'var(--brio-grey)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}
                        title="No longer active — shown because they logged time in this period"
                      >
                        Inactive
                      </span>
                    )}
                    {row.missingDays > 0 && (
                      <AlertCircle size={12} style={{ color: 'var(--brio-orange)' }} title={`${row.missingDays} missing day(s)`} />
                    )}
                  </span>
                </td>
                {row.dailyHours.map((h, i) => (
                  <td key={i} style={{
                    textAlign: 'right',
                    ...(h === 0 ? dimStyle : {}),
                    ...(dayColumns[i]?.isHoliday ? { background: 'var(--brio-gold-light)' } : {}),
                  }}>
                    {h === 0 ? '–' : formatHours(h)}
                  </td>
                ))}
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatHours(row.weekLogged)}</td>
                <td style={{ textAlign: 'right' }}>{formatHours(row.weekAllocated)}</td>
                <td style={{ textAlign: 'right' }} title={`Base: ${row.baseContractedHours}h/month`}>
                  {formatHours(row.weekContracted)}
                </td>
                <td style={{ textAlign: 'right', ...varianceStyle(row.vsAllocated) }}>{formatVariance(row.vsAllocated)}</td>
                <td style={{ textAlign: 'right', ...varianceStyle(row.vsContracted) }}>{formatVariance(row.vsContracted)}</td>
                <td style={{ textAlign: 'center' }}>
                  <BrioBadge variant={statusToBadgeVariant(row.status)}>
                    {Math.round(row.completionPct)}%
                  </BrioBadge>
                </td>
              </tr>
              {/* Drilldown: project rows */}
              {isExpanded && row.projectRows?.map(pr => (
                <tr key={`${row.userId}-${pr.projectId}`} style={{ background: '#fafafa' }}>
                  <td style={{ ...stickyTdStyle, paddingLeft: '28px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <BrioColorIndicator color={pr.projectColor} size="compact" />
                      <span style={{ fontSize: '10px' }}>{pr.projectName}</span>
                    </span>
                  </td>
                  {pr.dailyHours.map((h, i) => (
                    <td key={i} style={{
                      textAlign: 'right', fontSize: '10px',
                      ...(h === 0 ? dimStyle : {}),
                    }}>
                      {h === 0 ? '–' : formatHours(h)}
                    </td>
                  ))}
                  <td style={{ textAlign: 'right', fontSize: '10px', fontWeight: 600 }}>{formatHours(pr.total)}</td>
                  <td style={{ textAlign: 'right', fontSize: '10px' }}>{formatHours(pr.allocated)}</td>
                  <td colSpan={4}></td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </BrioTable>
  );
}

// ═══════════════════════════════════════════════════════════
// WEEKLY PROJECT TABLE
// ═══════════════════════════════════════════════════════════

function WeeklyProjectTable({ rows, dayColumns, expandedRows, toggleExpand, tableProps, varianceStyle, dimStyle, stickyThStyle, stickyTdStyle }) {
  return (
    <BrioTable {...tableProps}>
      <thead>
        <MonthGroupHeaderRow cols={dayColumns} summaryCount={2} stickyThStyle={stickyThStyle} />
        <tr>
          <th style={stickyThStyle}>Project</th>
          {dayColumns.map(col => (
            <th key={col.date} style={{
              textAlign: 'right', whiteSpace: 'nowrap',
              ...(col.isWeekend ? { color: 'var(--brio-grey)', opacity: 0.7 } : {}),
            }}>
              {col.label}
            </th>
          ))}
          <th style={{ textAlign: 'right' }}>Total</th>
          <th style={{ textAlign: 'right' }}>Alloc</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const isExpanded = expandedRows.has(row.projectId);
          return (
            <React.Fragment key={row.projectId}>
              <tr
                onClick={() => toggleExpand(row.projectId)}
                style={{ cursor: 'pointer', background: '#fff' }}
              >
                <td style={stickyTdStyle}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <BrioColorIndicator color={row.projectColor} size="compact" />
                    {row.projectName}
                  </span>
                </td>
                {row.dailyHours.map((h, i) => (
                  <td key={i} style={{ textAlign: 'right', ...(h === 0 ? dimStyle : {}) }}>
                    {h === 0 ? '–' : formatHours(h)}
                  </td>
                ))}
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatHours(row.total)}</td>
                <td style={{ textAlign: 'right' }}>{formatHours(row.allocated)}</td>
              </tr>
              {isExpanded && row.userRows?.map(ur => (
                <tr key={`${row.projectId}-${ur.userId}`} style={{ background: '#fafafa' }}>
                  <td style={{ ...stickyTdStyle, paddingLeft: '28px' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <BrioColorIndicator color={ur.teamColor} size="compact" />
                      <span style={{ fontSize: '10px' }}>{ur.userName}</span>
                    </span>
                  </td>
                  {ur.dailyHours.map((h, i) => (
                    <td key={i} style={{
                      textAlign: 'right', fontSize: '10px',
                      ...(h === 0 ? dimStyle : {}),
                    }}>
                      {h === 0 ? '–' : formatHours(h)}
                    </td>
                  ))}
                  <td style={{ textAlign: 'right', fontSize: '10px', fontWeight: 600 }}>{formatHours(ur.total)}</td>
                  <td style={{ textAlign: 'right', fontSize: '10px' }}>{formatHours(ur.allocated)}</td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
      </tbody>
    </BrioTable>
  );
}

// ═══════════════════════════════════════════════════════════
// MONTHLY TEAM TABLE
// ═══════════════════════════════════════════════════════════

function MonthlyTeamTable({ rows, weekColumns, expandedRows, toggleExpand, tableProps, varianceStyle, SortHeader, stickyThStyle, stickyTdStyle }) {
  return (
    <BrioTable {...tableProps}>
      <thead>
        <MonthGroupHeaderRow cols={weekColumns} summaryCount={6} stickyThStyle={stickyThStyle} />
        <tr>
          <SortHeader field="name" align="left" style={stickyThStyle}>Name</SortHeader>
          {weekColumns.map(col => (
            <th key={col.weekStart} style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: '10px' }}>
              {col.label}
            </th>
          ))}
          <SortHeader field="total">Total</SortHeader>
          <th style={{ textAlign: 'right' }}>Alloc</th>
          <th style={{ textAlign: 'right' }} title="Contracted hours adjusted for approved leave and join/end dates">Target</th>
          <SortHeader field="vsAlloc">vs Alloc</SortHeader>
          <SortHeader field="vsCntrt">vs Trgt</SortHeader>
          <SortHeader field="status">Status</SortHeader>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.userId} style={{ background: '#fff' }}>
            <td style={stickyTdStyle}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <BrioColorIndicator color={row.teamColor} size="compact" />
                {row.userName}
                {row.isInactive && (
                  <span
                    style={{ fontSize: '10px', color: 'var(--brio-grey)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}
                    title="No longer active — shown because they logged time in this period"
                  >
                    Inactive
                  </span>
                )}
              </span>
            </td>
            {row.weekTotals?.map((h, i) => (
              <td key={i} style={{ textAlign: 'right', ...(h === 0 ? { color: 'var(--brio-grey)', opacity: 0.5 } : {}) }}>
                {h === 0 ? '–' : formatHours(h)}
              </td>
            ))}
            <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatHours(row.monthLogged)}</td>
            <td style={{ textAlign: 'right' }}>{formatHours(row.monthAllocated)}</td>
            <td style={{ textAlign: 'right' }} title={`Base: ${row.baseContractedHours}h/month`}>
              {formatHours(row.monthContracted)}
            </td>
            <td style={{ textAlign: 'right', ...varianceStyle(row.vsAllocated) }}>{formatVariance(row.vsAllocated)}</td>
            <td style={{ textAlign: 'right', ...varianceStyle(row.vsContracted) }}>{formatVariance(row.vsContracted)}</td>
            <td style={{ textAlign: 'center' }}>
              <BrioBadge variant={statusToBadgeVariant(row.status)}>
                {Math.round(row.completionPct)}%
              </BrioBadge>
            </td>
          </tr>
        ))}
      </tbody>
    </BrioTable>
  );
}

// ═══════════════════════════════════════════════════════════
// MONTHLY PROJECT TABLE
// ═══════════════════════════════════════════════════════════

function MonthlyProjectTable({ rows, weekColumns, expandedRows, toggleExpand, tableProps, stickyThStyle, stickyTdStyle }) {
  return (
    <BrioTable {...tableProps}>
      <thead>
        <MonthGroupHeaderRow cols={weekColumns} summaryCount={2} stickyThStyle={stickyThStyle} />
        <tr>
          <th style={stickyThStyle}>Project</th>
          {weekColumns.map(col => (
            <th key={col.weekStart} style={{ textAlign: 'right', whiteSpace: 'nowrap', fontSize: '10px' }}>
              {col.label}
            </th>
          ))}
          <th style={{ textAlign: 'right' }}>Total</th>
          <th style={{ textAlign: 'right' }}>Alloc</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.projectId} style={{ background: '#fff' }}>
            <td style={stickyTdStyle}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <BrioColorIndicator color={row.projectColor} size="compact" />
                {row.projectName}
              </span>
            </td>
            {row.weekTotals?.map((h, i) => (
              <td key={i} style={{ textAlign: 'right', ...(h === 0 ? { color: 'var(--brio-grey)', opacity: 0.5 } : {}) }}>
                {h === 0 ? '–' : formatHours(h)}
              </td>
            ))}
            <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatHours(row.total)}</td>
            <td style={{ textAlign: 'right' }}>{formatHours(row.allocated)}</td>
          </tr>
        ))}
      </tbody>
    </BrioTable>
  );
}

export default TimesheetOverviewPage;
